import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { Spinner } from "@via/components/loading/loading";
import { NOTIFICATION } from "@via/context/notifications-context";
import { useAuth } from "@via/hooks/useAuth";
import { useNotifications } from "@via/hooks/useNotifications";
import {
  useGetSavedAmazonCredentialsList,
  useGetSavedGoogleCredentialsList,
} from "@via/pages/data/data-sources/data-source-credentials.api";
import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants";
import { useVmetaProjectManagement } from "@via/pages/data/hooks/useVmetaProjectManagement";
import { ModalSize } from "@via/utils/constants";
import { classNames } from "@via/utils/helpers";
import { SelectOption } from "@via/utils/interfaces";
import { DataSourceProvider } from "common";
import { type JSX, useEffect, useMemo, useState } from "react";
import {
  fetchFileCollectionFields,
  useCreatePremiumDatasetFile,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import {
  buildFolderPathFilePayload,
  type FileFieldNameLabel,
} from "@via/pages/data/file-organizer/helpers";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import {
  useAmazonS3FolderList,
  useGoogleCloudFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  CLOUD_PROTOCOL,
  getCloudProtocol,
  parseCloudPath,
} from "@via/pages/data/premium-dataset/utils/helper";
import { formatSharedTrackerOptionLabel } from "@via/pages/data/utils/shared-tracker-option-label";
import {
  MembershipConnectionsWarning,
  useMembershipAssumeRoleConnections,
  useMembershipGoogleConnections,
} from "@via/pages/organization/membership-connections.tsx";
import { GoogleCredentialUsageScope } from "common";
import { getAxiosErrorMessage } from "@via/utils/helpers.tsx";

interface AddFolderPathModalProps {
  isOpen: boolean;
  datasetId: string;
  onClose: () => void;
  fileCanvasId?: string;
  isGuest?: boolean;
  onCreateSuccess?: (fileRecord: Record<string, unknown>) => void;
}

const deriveNameFromPath = (path: string): string => {
  const trimmed = path.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  const last = trimmed.split("/").filter(Boolean).pop();
  return last || trimmed;
};

export default function AddFolderPathModal({
  isOpen,
  datasetId,
  onClose,
  fileCanvasId,
  isGuest = false,
  onCreateSuccess,
}: AddFolderPathModalProps): JSX.Element {
  const { user } = useAuth();
  const { setNotification } = useNotifications();

  const mongoUserId = user?.vmeta?.userId;

  const [selectedTrackerViewType, setSelectedTrackerViewType] =
    useState<SampleTrackerViewType>(SampleTrackerViewType.My);
  const isMyTrackersView = selectedTrackerViewType === SampleTrackerViewType.My;

  const {
    vmetaProjectOptions,
    selectedVmetaProject,
    setSelectedVmetaProject,
    isLoading: isVmetaProjectsLoading,
    setSearch: debouncedHandleInputChange,
    fetchMoreOptions: fetchMoreVmetaProjects,
    createProject,
    resolvedInitialTrackerViewType,
  } = useVmetaProjectManagement({
    userId: mongoUserId,
    viewType: selectedTrackerViewType,
    enabled: isOpen,
    initialProjectId: fileCanvasId,
    resolveInitialProjectOwnership: true,
  });

  useEffect(() => {
    if (!isOpen || !resolvedInitialTrackerViewType) return;
    setSelectedTrackerViewType(resolvedInitialTrackerViewType);
  }, [isOpen, resolvedInitialTrackerViewType]);

  const [folderPath, setFolderPath] = useState("");
  const [hasTriedCreate, setHasTriedCreate] = useState(false);
  const cloudProvider = useMemo(
    () => getCloudProtocol(folderPath),
    [folderPath]
  );

  const folderPathHelp = useMemo(() => {
    const raw = folderPath.trim();
    if (!raw) {
      return {
        variant: "info" as const,
        message:
          "S3 (s3://) and Google Cloud Storage (gs://) only. Include bucket + at least two subfolders (e.g., gs://bucket/project/results).",
      };
    }

    if (
      cloudProvider !== CLOUD_PROTOCOL.S3 &&
      cloudProvider !== CLOUD_PROTOCOL.GS
    ) {
      return {
        variant: "error" as const,
        message: 'Only "s3://" and "gs://" folder paths are supported.',
      };
    }

    const withoutScheme = raw.replace(/^(s3|gs):\/\//i, "").replace(/\/+$/, "");
    const parts = withoutScheme.split("/").filter(Boolean); // [bucket, ...prefixParts]
    if (parts.length < 3) {
      return {
        variant: "error" as const,
        message: `Include at least two folder levels under the bucket (e.g. ${cloudProvider}://bucket/a/b).`,
      };
    }

    return { variant: "success" as const, message: "Path is valid." };
  }, [cloudProvider, folderPath]);

  const isFolderPathValid = folderPathHelp.variant !== "error";

  const { data: savedAmazonCredentials = [], isLoading: isAmazonCredsLoading } =
    useGetSavedAmazonCredentialsList();
  const { data: savedGoogleCredentials = [], isLoading: isGoogleCredsLoading } =
    useGetSavedGoogleCredentialsList();
  // Every active membership's saved connections, labeled by organization (#1135).
  const {
    connections: orgGoogleCredentials,
    failedOrganizationNames: failedGoogleOrganizations,
  } = useMembershipGoogleConnections(user, {
    usageScope: GoogleCredentialUsageScope.Dataset,
  });
  const {
    connections: orgAmazonCredentials,
    failedOrganizationNames: failedAmazonOrganizations,
  } = useMembershipAssumeRoleConnections(user);

  const amazonCredentialOptions = useMemo(
    () => [
      ...orgAmazonCredentials.map(c => ({
        label: c.label,
        value: c.id,
      })),
      { label: BUILT_IN_ACCESS_OPTION_LABEL, value: BUILT_IN_ACCESS_OPTION_ID },
      ...savedAmazonCredentials.map(c => ({ label: c.name, value: c.id })),
    ],
    [savedAmazonCredentials, orgAmazonCredentials]
  );

  const googleCredentialOptions = useMemo(
    () => [
      ...orgGoogleCredentials.map(c => ({
        label: c.label,
        value: c.id,
      })),
      { label: BUILT_IN_ACCESS_OPTION_LABEL, value: BUILT_IN_ACCESS_OPTION_ID },
      ...savedGoogleCredentials.map(c => ({
        label: c.name,
        value: c.id,
      })),
    ],
    [savedGoogleCredentials, orgGoogleCredentials]
  );

  const [amazonCredId, setAmazonCredId] = useState<SelectOption>({
    label: BUILT_IN_ACCESS_OPTION_LABEL,
    value: BUILT_IN_ACCESS_OPTION_ID,
  });
  const [googleCredId, setGoogleCredId] = useState<SelectOption>({
    label: BUILT_IN_ACCESS_OPTION_LABEL,
    value: BUILT_IN_ACCESS_OPTION_ID,
  });
  const [hasUserSelectedGoogleCredential, setHasUserSelectedGoogleCredential] =
    useState(false);
  const [hasUserSelectedAmazonCredential, setHasUserSelectedAmazonCredential] =
    useState(false);

  // Reset credential selections when provider changes
  useEffect(() => {
    if (cloudProvider === CLOUD_PROTOCOL.S3) {
      setGoogleCredId({
        label: BUILT_IN_ACCESS_OPTION_LABEL,
        value: BUILT_IN_ACCESS_OPTION_ID,
      });
      setHasUserSelectedGoogleCredential(false);
    } else if (cloudProvider === CLOUD_PROTOCOL.GS) {
      setAmazonCredId({
        label: BUILT_IN_ACCESS_OPTION_LABEL,
        value: BUILT_IN_ACCESS_OPTION_ID,
      });
      setHasUserSelectedAmazonCredential(false);
    }
  }, [cloudProvider]);

  useEffect(() => {
    if (cloudProvider !== CLOUD_PROTOCOL.S3) return;
    if (hasUserSelectedAmazonCredential) return;
    if (Number(amazonCredId.value) !== BUILT_IN_ACCESS_OPTION_ID) return;
    if (orgAmazonCredentials.length === 0) return;

    setAmazonCredId({
      label: orgAmazonCredentials[0].label,
      value: orgAmazonCredentials[0].id,
    });
  }, [
    cloudProvider,
    amazonCredId.value,
    hasUserSelectedAmazonCredential,
    orgAmazonCredentials,
  ]);

  useEffect(() => {
    if (cloudProvider !== CLOUD_PROTOCOL.GS) return;
    if (hasUserSelectedGoogleCredential) return;
    if (Number(googleCredId.value) !== BUILT_IN_ACCESS_OPTION_ID) return;
    if (orgGoogleCredentials.length === 0) return;

    setGoogleCredId({
      label: orgGoogleCredentials[0].label,
      value: orgGoogleCredentials[0].id,
    });
  }, [
    cloudProvider,
    googleCredId.value,
    hasUserSelectedGoogleCredential,
    orgGoogleCredentials,
  ]);

  const { mutateAsync: createPremiumDatasetFile, isPending: isCreating } =
    useCreatePremiumDatasetFile();

  const [localError, setLocalError] = useState<string | null>(null);
  const [isValidatingExistence, setIsValidatingExistence] = useState(false);

  const { mutateAsync: listS3Objects } = useAmazonS3FolderList();
  const { mutateAsync: listGcsObjects } = useGoogleCloudFolderList();

  const validateCloudPathExists = async (path: string): Promise<boolean> => {
    const parsed = parseCloudPath(path);
    if (!parsed) {
      return false;
    }

    const savedCredentialId =
      parsed.provider === DataSourceProvider.Amazon
        ? Number(amazonCredId.value)
        : parsed.provider === DataSourceProvider.Google
          ? Number(googleCredId.value)
          : null;
    if (savedCredentialId == null || Number.isNaN(savedCredentialId)) {
      return false;
    }

    const listObjects = async (recursive: boolean) => {
      if (parsed.provider === DataSourceProvider.Amazon) {
        return await listS3Objects({
          savedCredentialId,
          bucketName: parsed.bucket,
          prefix: parsed.prefix,
          recursive,
        });
      }
      return await listGcsObjects({
        savedCredentialId,
        bucketName: parsed.bucket,
        prefix: parsed.prefix,
        recursive,
      });
    };

    // File-exists check: object key matches exactly.
    const exact = await listObjects(false);
    if ((exact?.Contents ?? []).some(o => o.Key === parsed.prefix)) {
      return true;
    }

    // Folder-exists check: at least one object under the prefix (server normalizes to prefix + "/").
    const under = await listObjects(true);
    return (under?.Contents ?? []).length > 0;
  };

  const handleClose = () => {
    setFolderPath("");
    setLocalError(null);
    setHasTriedCreate(false);
    setHasUserSelectedGoogleCredential(false);
    setHasUserSelectedAmazonCredential(false);
    setAmazonCredId({
      label: BUILT_IN_ACCESS_OPTION_LABEL,
      value: BUILT_IN_ACCESS_OPTION_ID,
    });
    setGoogleCredId({
      label: BUILT_IN_ACCESS_OPTION_LABEL,
      value: BUILT_IN_ACCESS_OPTION_ID,
    });
    onClose();
  };

  const handleCreate = async () => {
    setLocalError(null);
    setHasTriedCreate(true);
    const path = folderPath.trim();
    if (!selectedVmetaProject?.value) {
      setLocalError("Please select a Sample Tracker.");
      return;
    }
    if (!path) {
      setLocalError("Please enter a folder path.");
      return;
    }
    if (!isFolderPathValid) {
      return;
    }

    const name = deriveNameFromPath(path);
    const provider =
      cloudProvider === CLOUD_PROTOCOL.S3
        ? DataSourceProvider.Amazon
        : cloudProvider === CLOUD_PROTOCOL.GS
          ? DataSourceProvider.Google
          : null;

    const credentialId =
      cloudProvider === CLOUD_PROTOCOL.S3
        ? Number(amazonCredId.value)
        : cloudProvider === CLOUD_PROTOCOL.GS
          ? Number(googleCredId.value)
          : null;

    try {
      setIsValidatingExistence(true);
      const exists = await validateCloudPathExists(path);
      if (!exists) {
        setLocalError(
          "Path not found (or empty). Please verify the bucket, path, and credentials."
        );
        return;
      }

      const canvasId = selectedVmetaProject.value as string;
      // Unknown field list: the source is left out and the save still works.
      const fileFields: FileFieldNameLabel[] | undefined =
        await fetchFileCollectionFields(canvasId).catch(() => undefined);
      const payload = buildFolderPathFilePayload({
        path,
        name,
        provider,
        credentialId,
        fileFields,
      });
      await createPremiumDatasetFile({
        id: datasetId,
        canvasId,
        file: payload,
      });

      if (isGuest && onCreateSuccess) {
        const fileRecord: Record<string, unknown> = {
          name,
          file1: path,
          google_cre_id:
            cloudProvider === CLOUD_PROTOCOL.GS
              ? Number(googleCredId.value)
              : null,
          amazon_cre_id:
            cloudProvider === CLOUD_PROTOCOL.S3
              ? Number(amazonCredId.value)
              : null,
          // What was actually sent: null when the gate held the source back.
          data_source_provider: (payload.Source as string | undefined) ?? null,
        };
        handleClose();
        onCreateSuccess(fileRecord);
      } else {
        handleClose();
      }
    } catch (err: any) {
      const message =
        getAxiosErrorMessage(err) ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add folder path. Please try again.";
      setLocalError(message);
      setNotification({
        id: "add-folder-path-failed",
        message: "Failed to add folder path",
        details: message,
        variant: NOTIFICATION.ERROR,
      });
    } finally {
      setIsValidatingExistence(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      title="Add Folder for Download Only"
      size={ModalSize.Large}
      width={900}
      showButtons={false}>
      <div className="my-4 space-y-5">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Add a cloud folder you want to download. Don&apos;t use this window to
          add folder for Runs (use &quot;Browse &amp; Add Files&quot; instead)
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Select Sample Tracker
          </label>
          <div className="mb-3">
            <span className="isolate inline-flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() =>
                  setSelectedTrackerViewType(SampleTrackerViewType.My)
                }
                className={classNames(
                  "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap rounded-l-md min-w-[110px]",
                  selectedTrackerViewType === SampleTrackerViewType.My
                    ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                    : "bg-white"
                )}>
                My Trackers
              </button>
              <button
                type="button"
                onClick={() =>
                  setSelectedTrackerViewType(SampleTrackerViewType.Shared)
                }
                className={classNames(
                  "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap -ml-px min-w-[150px] rounded-r-md",
                  selectedTrackerViewType === SampleTrackerViewType.Shared
                    ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                    : "bg-white"
                )}>
                Shared With Me
              </button>
            </span>
          </div>

          <SelectComponent
            name="vmetaProjectId"
            options={vmetaProjectOptions}
            value={selectedVmetaProject}
            onChange={selected =>
              setSelectedVmetaProject(selected as SelectOption)
            }
            formatOptionLabel={(option, context) =>
              formatSharedTrackerOptionLabel(
                option,
                context,
                selectedTrackerViewType
              )
            }
            isLoading={isVmetaProjectsLoading}
            isCreatable={isMyTrackersView}
            showCreateHint={isMyTrackersView}
            onCreateOption={isMyTrackersView ? createProject : undefined}
            placeholder={
              isMyTrackersView
                ? "Choose sample tracker or type to create"
                : "Choose a sample tracker shared with you"
            }
            enableClear={false}
            onFetchMoreOptions={fetchMoreVmetaProjects}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Cloud Folder Path
          </label>
          <input
            type="text"
            value={folderPath}
            onChange={e => setFolderPath(e.target.value)}
            placeholder='Examples: "s3://bucket/project/exports", "gs://bucket/project/exports"'
            className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
          <p
            className={classNames(
              "mt-2 text-sm",
              folderPathHelp.variant === "success"
                ? "text-green-700"
                : hasTriedCreate && folderPathHelp.variant === "error"
                  ? "text-red-600"
                  : "text-gray-500"
            )}>
            {folderPathHelp.message}
          </p>
        </div>

        {cloudProvider === "s3" && (
          <div>
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              AWS credentials
            </label>
            <SelectComponent
              name="amazonCredentialId"
              options={amazonCredentialOptions}
              value={amazonCredId}
              onChange={selected => {
                setHasUserSelectedAmazonCredential(true);
                setAmazonCredId(selected as SelectOption);
              }}
              isLoading={isAmazonCredsLoading}
              enableClear={false}
              placeholder="Choose AWS credentials"
            />
            <MembershipConnectionsWarning names={failedAmazonOrganizations} />
          </div>
        )}

        {cloudProvider === "gs" && (
          <div>
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Google Cloud credentials
            </label>
            <SelectComponent
              name="googleCredentialId"
              options={googleCredentialOptions}
              value={googleCredId}
              onChange={selected => {
                setHasUserSelectedGoogleCredential(true);
                setGoogleCredId(selected as SelectOption);
              }}
              isLoading={isGoogleCredsLoading}
              enableClear={false}
              placeholder="Choose Google Cloud credentials"
            />
            <MembershipConnectionsWarning names={failedGoogleOrganizations} />
          </div>
        )}

        {localError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {localError}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              !selectedVmetaProject ||
              !folderPath.trim() ||
              isCreating ||
              isValidatingExistence
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            <Spinner loading={isCreating || isValidatingExistence}>
              Download
            </Spinner>
          </button>
        </div>
      </div>
    </Modal>
  );
}
